import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';

// Family / group accounts — one login holding several patients.
//
//   GUNA   login + patient record      <- primary
//    |- BALA   patient record only     <- member, no login, no email
//    |- MEENA  patient record only
//
// Backend: /api/patients/family (see api/controllers/familyAccountController.js).
// The whole feature is gated platform-wide by Super Admin; `enabled` on the
// response says whether the "add member" button should show. The API refuses
// regardless, so hiding the button is the polite half rather than the control.

const getSerializableError = (error) => {
    if (!error) return 'An unknown error occurred';
    if (typeof error === 'string') return error;
    if (error.response) {
        return error.response.data?.message || error.message || `Request failed with status code ${error.response.status}`;
    }
    return error.message || 'An error occurred';
};

export const getMyFamily = createAsyncThunk(
    'family/getMyFamily',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/patients/family`);
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// Doctor / admin looking at one patient's family. Guarded by the connection
// gate server-side: only a patient their hospital actually treats.
export const getFamilyForPatient = createAsyncThunk(
    'family/getFamilyForPatient',
    async (patientId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/patients/${patientId}/family`);
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// primaryPatientId is only read for doctor/admin callers — a patient always
// adds to their own family whatever they send.
export const addFamilyMember = createAsyncThunk(
    'family/addFamilyMember',
    async (payload, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/patients/family`, payload);
            if (!payload?.primaryPatientId) dispatch(getMyFamily());
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// Switching profile re-issues the TOKEN — it is not a screen-only change, or
// anyone could ask for anyone's records. The new tokens replace the stored ones
// so every subsequent request acts as the chosen person.
export const switchProfile = createAsyncThunk(
    'family/switchProfile',
    async (patientId, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/patients/family/switch`, { patientId });
            const { accessToken, refreshToken, activePatientId, profile, isPrimary } = res.data || {};
            if (accessToken) localStorage.setItem('accessToken', accessToken);
            if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

            if (activePatientId) {
                // THIS IS WHAT ACTUALLY SWITCHES THE APP.
                //
                // Every screen identifies the current patient from
                // localStorage.ID — appointments, profile, chat, payments, all
                // of it. Storing only a new token changed who the SERVER
                // thought we were while every page kept asking for the old
                // patient's data by id, so the app still showed the primary and
                // a booking landed on the wrong person. Both have to move
                // together.
                localStorage.setItem('ID', activePatientId);
                localStorage.setItem('activePatientId', activePatientId);
                localStorage.setItem('activePatientIsPrimary', String(!!isPrimary));
                const label = [profile?.name, profile?.surname].filter(Boolean).join(' ').trim();
                localStorage.setItem('activePatientName', label);
                if (profile?.image || profile?.imagePath) {
                    localStorage.setItem('activePatientImage', profile.image || profile.imagePath);
                }

                // MOST RECENT FIRST. A family of eight makes the switcher a
                // scroll, and people move between the same two or three all day.
                // Stored here rather than derived from the family list because
                // "recently used" is a fact about this person's habits, not
                // about the account order.
                try {
                    const prev = JSON.parse(localStorage.getItem('recentProfiles') || '[]');
                    const next = [activePatientId, ...prev.filter((id) => id !== activePatientId)].slice(0, 6);
                    localStorage.setItem('recentProfiles', JSON.stringify(next));
                } catch {
                    localStorage.setItem('recentProfiles', JSON.stringify([activePatientId]));
                }
            }
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// Gives a member their own login. Their records stay with them — nothing is
// copied — and the primary loses access at that moment.
export const graduateMember = createAsyncThunk(
    'family/graduateMember',
    async ({ patientId, email, password }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(
                `${baseURL}/patients/family/${patientId}/graduate`, { email, password }
            );
            dispatch(getMyFamily());
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// Ask for a verification code. The member's email only counts once proven, and
// proving it is what earns them their own login.
export const sendFamilyOtp = createAsyncThunk(
    'family/sendFamilyOtp',
    async (email, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/auth/send-otp`, { email });
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// Ask for a code on a MOBILE number. Separate from the email code: the split
// screen verifies both, and each has to be proven on its own.
export const sendFamilyPhoneOtp = createAsyncThunk(
    'family/sendFamilyPhoneOtp',
    async (phone, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/auth/send-otp`, { phone });
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// Split: one call, two paths. Someone who ALREADY has a login is simply
// detached — they proved their contact when the login was made, so asking again
// would be theatre. Anyone else must prove an email and a mobile first, because
// that is what earns them an account of their own.
export const splitMember = createAsyncThunk(
    'family/splitMember',
    async ({ patientId, email, emailOtp, phone, phoneOtp, keepInFamily, permission }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            // `keepInFamily` decides ONLY whether they also leave the group. The
            // verified contact details and the new sign-in are saved either way —
            // proving an email and a mobile is work the person actually did, and
            // discarding it because they chose to stay would be silent data loss.
            const res = await axiosInstance.post(
                `${baseURL}/patients/family/${patientId}/split`,
                {
                    email, emailOtp, phone, phoneOtp, keepInFamily: !!keepInFamily,
                    // What they share back once they hold their own sign-in
                    // ('read' | 'readwrite') — only with keepInFamily, since
                    // leaving revokes the edge (owner, 2026-09-09).
                    ...(keepInFamily ? { permission: permission || 'readwrite' } : {}),
                }
            );
            dispatch(getMyFamily());
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// Remove a member who has no login of their own. Takes them out of the group;
// their records are never deleted.
export const removeMember = createAsyncThunk(
    'family/removeMember',
    async (patientId, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.delete(`${baseURL}/patients/family/${patientId}`);
            dispatch(getMyFamily());
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// A person can always leave a family that can read their medical records.
export const leaveFamily = createAsyncThunk(
    'family/leaveFamily',
    async (_, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/patients/family/leave`);
            dispatch(getMyFamily());
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// ---------------------------------------------------------------------------
// Phase 5 — asking someone who ALREADY has an account to join the family.
//
// Adding a new person needs nobody's permission: they have no account and no
// records. Asking an existing patient is a different thing entirely, because
// accepting lets another household read and manage their medical history. So it
// is a request they answer, never a link the sender can simply make.

// Results come back MASKED from the server (r***i@gmail.com, 98***1234) — enough
// to recognise the right person, never enough to harvest contact details. Do not
// "improve" this by asking the API for the real values.
export const searchFamilyCandidates = createAsyncThunk(
    'family/searchCandidates',
    async (q, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(
                `${baseURL}/patients/family/search`, { params: { q } }
            );
            return res.data?.results || [];
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

export const sendFamilyRequest = createAsyncThunk(
    'family/sendRequest',
    async ({ toPatientId, relationship, permission }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(
                `${baseURL}/patients/family/requests`,
                { toPatientId, relationship, permission: permission || 'read' }
            );
            dispatch(getFamilyRequests());
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// `pending` is what is waiting on ME — that is the badge count. `history` is
// everything else, both directions, so each side can see what was asked and
// what happened.
export const getFamilyRequests = createAsyncThunk(
    'family/getRequests',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/patients/family/requests`);
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

export const respondToFamilyRequest = createAsyncThunk(
    'family/respondToRequest',
    async ({ requestId, accept }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(
                `${baseURL}/patients/family/requests/${requestId}/respond`, { accept }
            );
            dispatch(getFamilyRequests());
            // Accepting changes who is in the family, so the group has to be
            // re-read or the switcher keeps showing the old list.
            if (accept) dispatch(getMyFamily());
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// ---------------------------------------------------------------------------
// Sharing. Access has a DIRECTION and a PERMISSION now, so two lists exist that
// used to be one: who can open my account, and what I can open. They are not
// mirrors of each other and must never be rendered as if they were.

export const getShares = createAsyncThunk(
    'family/getShares',
    async (_, { rejectWithValue }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.get(`${baseURL}/patients/shares`);
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// Giving access to MY account. No approval — it is my record to give.
export const createShare = createAsyncThunk(
    'family/createShare',
    async ({ toPatientId, permission }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.post(`${baseURL}/patients/shares`, { toPatientId, permission });
            dispatch(getShares());
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

// Raising or lowering. The owner never needs approval, in either direction.
export const updateShare = createAsyncThunk(
    'family/updateShare',
    async ({ shareId, permission }, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.patch(`${baseURL}/patients/shares/${shareId}`, { permission });
            dispatch(getShares());
            dispatch(getMyFamily());
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

export const revokeShare = createAsyncThunk(
    'family/revokeShare',
    async (shareId, { rejectWithValue, dispatch }) => {
        const axiosInstance = useAxiosAuth();
        try {
            const res = await axiosInstance.delete(`${baseURL}/patients/shares/${shareId}`);
            dispatch(getShares());
            dispatch(getMyFamily());
            return res.data;
        } catch (error) {
            return rejectWithValue(getSerializableError(error));
        }
    }
);

const familySlice = createSlice({
    name: 'family',
    initialState: {
        primary: null,
        members: [],
        contact: null,
        activePatientId: null,
        // Whether Super Admin has the feature switched on platform-wide.
        enabled: false,
        relationships: [],
        consentRequiredFor: [],
        maxMembers: 10,
        loading: false,
        saving: false,
        error: null,
        // Doctor/admin view of somebody else's family, kept separate so it can't
        // overwrite the caller's own.
        viewed: null,
        viewedLoading: false,
        // Phase 5 — merge requests.
        requestsPending: [],
        requestsHistory: [],
        requestsLoading: false,
        searchResults: [],
        searching: false,
        // Two separate lists, deliberately: sharing is one-way, so these are not
        // two views of the same thing.
        sharedWithOthers: [],
        sharedWithMe: [],
        sharesLoading: false,
    },
    reducers: {
        clearViewedFamily: (state) => { state.viewed = null; },
        clearCandidateSearch: (state) => { state.searchResults = []; },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getMyFamily.pending, (state) => { state.loading = true; state.error = null; })
            .addCase(getMyFamily.fulfilled, (state, action) => {
                const p = action.payload || {};
                state.loading = false;
                state.primary = p.primary || null;
                state.members = p.members || [];
                state.contact = p.contact || null;
                state.activePatientId = p.activePatientId || null;
                state.enabled = !!p.enabled;
                state.relationships = p.relationships || [];
                state.consentRequiredFor = p.consentRequiredFor || [];
                state.maxMembers = p.maxMembers || 10;
            })
            .addCase(getMyFamily.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(addFamilyMember.pending, (state) => { state.saving = true; })
            .addCase(addFamilyMember.fulfilled, (state) => { state.saving = false; })
            .addCase(addFamilyMember.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload;
            })
            .addCase(graduateMember.pending, (state) => { state.saving = true; })
            .addCase(graduateMember.fulfilled, (state) => { state.saving = false; })
            .addCase(graduateMember.rejected, (state, action) => {
                state.saving = false;
                state.error = action.payload;
            })
            .addCase(getFamilyForPatient.pending, (state) => { state.viewedLoading = true; })
            .addCase(getFamilyForPatient.fulfilled, (state, action) => {
                state.viewedLoading = false;
                state.viewed = action.payload || null;
            })
            .addCase(getFamilyForPatient.rejected, (state) => {
                state.viewedLoading = false;
                state.viewed = null;
            })
            .addCase(getFamilyRequests.pending, (state) => { state.requestsLoading = true; })
            .addCase(getFamilyRequests.fulfilled, (state, action) => {
                state.requestsLoading = false;
                state.requestsPending = action.payload?.pending || [];
                state.requestsHistory = action.payload?.history || [];
            })
            .addCase(getFamilyRequests.rejected, (state) => {
                state.requestsLoading = false;
                // Left as-is rather than cleared: a failed refresh should not
                // make a real pending request silently disappear from the badge.
            })
            .addCase(searchFamilyCandidates.pending, (state) => { state.searching = true; })
            .addCase(searchFamilyCandidates.fulfilled, (state, action) => {
                state.searching = false;
                state.searchResults = action.payload || [];
            })
            .addCase(searchFamilyCandidates.rejected, (state) => {
                state.searching = false;
                state.searchResults = [];
            })
            .addCase(getShares.pending, (state) => { state.sharesLoading = true; })
            .addCase(getShares.fulfilled, (state, action) => {
                state.sharesLoading = false;
                state.sharedWithOthers = action.payload?.sharedWithOthers || [];
                state.sharedWithMe = action.payload?.sharedWithMe || [];
            })
            .addCase(getShares.rejected, (state) => { state.sharesLoading = false; });
    },
});

export const { clearViewedFamily, clearCandidateSearch } = familySlice.actions;
export default familySlice.reducer;
